import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { ulid } from "ulid";

import { AddProductVariantSection } from "@/features/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Paper, Stack, Typography } from "@mui/material";

import { GeneralInformationSection } from "../customer-claim-form/general-information-section";
import { ProductVariantsSection } from "../customer-claim-form/product-variants-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./receive-customer-claim-form-constants";

export const ReceiveCustomerClaimForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
}) => {
  const methods = useForm({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { handleSubmit, formState, watch, control } = methods;

  const { append } = useFieldArray({
    control,
    name: "customerClaimProductVariants",
  });

  const seller = watch("seller");
  const warehouse = watch("warehouse");
  const customerClaimProductVariants = watch("customerClaimProductVariants");

  const handleAddProductVariant = (productVariant, product, quantity) => {
    append({
      id: ulid(),
      productId: product.id,
      sourceableId: null,
      sourceableType: null,
      name: productVariant.name,
      sku: productVariant.sku,
      quantity: Number(quantity),
      unitOfMeasurement: product.unitOfMeasurement.name,
      note: "",
    });
  };

  const handleFormSubmit = async (formData) => {
    const payload = {
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      sellerId: formData.seller.id,
      buyerableId: formData.buyer.id,
      buyerableType: formData.buyerableType,
      warehouseId: formData.warehouse.id,
      note: formData.note || null,
      customerClaimProductVariants: formData.customerClaimProductVariants.map(
        (variant) => ({
          productId: variant.productId,
          sourceableId: variant.sourceableId || null,
          sourceableType: variant.sourceableType || null,
          name: variant.name,
          sku: variant.sku,
          quantity: variant.quantity,
          note: variant.note || null,
        })
      ),
    };

    await onSubmit(payload);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        onSubmit={handleSubmit(handleFormSubmit)}
        onKeyDown={handleKeyDown}
        spacing={2.5}
      >
        <GeneralInformationSection editMode={editMode} />

        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            <Typography variant="h6">Cari/Pilih Barang & Jasa</Typography>
            <AddProductVariantSection
              outletId={seller?.id}
              onAdded={handleAddProductVariant}
              disabled={!warehouse}
            />

            <Divider />
            <Typography variant="h6">
              {customerClaimProductVariants.length} Barang
            </Typography>
            <ProductVariantsSection />
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Box>
      </Stack>
    </FormProvider>
  );
};

ReceiveCustomerClaimForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
};
